/**
 * ProcessingLog Component
 * Real-time diagnostic feed display with enhanced accessibility.
 *
 * Entries are memoised — only the new tail entry ever re-renders or animates.
 * Previous entries are stable once mounted.
 *
 * Collapsed mode (during active processing): shows only the latest log entry
 * in a compact single-line strip.  An expand toggle lets the user view the
 * full log if needed.
 *
 * Expanded mode (after processing / when explicitly toggled): full scrollable
 * log as before.
 */

import React, { useEffect, useRef, useState } from 'react';
import { TranscriptionLog } from '../types';

interface ProcessingLogProps {
  logs: TranscriptionLog[];
  /** When true the component defaults to collapsed (single-line) mode */
  isProcessing?: boolean;
}

// ── Pure helpers — defined outside component so they are stable references ──

function getLogIcon(type: TranscriptionLog['type']): string {
  switch (type) {
    case 'success': return '✓';
    case 'warning': return '⚠';
    case 'error':   return '✕';
    default:        return '→';
  }
}

function getLogColorClass(type: TranscriptionLog['type']): string {
  switch (type) {
    case 'error':   return 'text-rose-400';
    case 'success': return 'text-emerald-400';
    case 'warning': return 'text-amber-400';
    default:        return 'text-indigo-400';
  }
}

function getAriaLabel(type: TranscriptionLog['type']): string {
  switch (type) {
    case 'error':   return 'Error';
    case 'success': return 'Success';
    case 'warning': return 'Warning';
    default:        return 'Info';
  }
}

// ── Memoised entry ───────────────────────────────────────────────────────────

interface LogEntryProps {
  log: TranscriptionLog;
  isLast: boolean;
  lastLogRef: React.RefObject<HTMLDivElement | null>;
}

const LogEntry = React.memo<LogEntryProps>(({ log, isLast, lastLogRef }) => (
  <div
    ref={isLast ? lastLogRef : null}
    className="flex gap-3 sm:gap-6 text-[10px] sm:text-[11px] font-mono leading-relaxed border-b border-slate-900/40 pb-1 sm:pb-1.5 animate-in slide-up"
  >
    <span
      className="text-slate-700 whitespace-nowrap opacity-40 min-w-[55px] sm:min-w-[70px] tabular-nums"
      aria-hidden="true"
    >
      [{log.timestamp}]
    </span>
    <div className={`flex gap-2 sm:gap-3 ${getLogColorClass(log.type)}`}>
      <span
        className="font-bold opacity-80"
        aria-label={getAriaLabel(log.type)}
        role="img"
      >
        {getLogIcon(log.type)}
      </span>
      <span className="transition-colors duration-300">{log.message}</span>
    </div>
  </div>
));

LogEntry.displayName = 'LogEntry';

// ── Container ────────────────────────────────────────────────────────────────

export const ProcessingLog: React.FC<ProcessingLogProps> = ({ logs, isProcessing = false }) => {
  const lastLogRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);

  // Collapse automatically when processing starts; expand when done
  useEffect(() => {
    if (isProcessing) setExpanded(false);
  }, [isProcessing]);

  useEffect(() => {
    if (lastLogRef.current && expanded) {
      lastLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [logs, expanded]);

  const latestLog = logs[logs.length - 1];
  const showCollapsed = isProcessing && !expanded;

  // ── Collapsed mode ──────────────────────────────────────────────────────
  if (showCollapsed) {
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 rounded-2xl bg-slate-950 border border-slate-900 text-[10px] sm:text-[11px] font-mono"
        role="status"
        aria-live="polite"
        aria-label="Current processing step"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="shrink-0 w-2 h-2 rounded-full bg-[#00d4b8] animate-pulse" aria-hidden="true" />
          {latestLog ? (
            <span className={`truncate ${getLogColorClass(latestLog.type)}`}>
              {getLogIcon(latestLog.type)} {latestLog.message}
            </span>
          ) : (
            <span className="text-slate-600">Processing…</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="shrink-0 text-[9px] text-slate-500 hover:text-slate-300 uppercase tracking-widest transition-colors whitespace-nowrap"
          aria-label="Show full diagnostic log"
        >
          Show log ↓
        </button>
      </div>
    );
  }

  // ── Expanded mode ───────────────────────────────────────────────────────
  return (
    <div
      className="bg-slate-950 rounded-[24px] sm:rounded-[32px] p-6 sm:p-8 h-64 sm:h-80 overflow-y-auto border border-slate-900 shadow-xl relative scroll-smooth"
      role="log"
      aria-label="Processing log"
      aria-live="polite"
      aria-relevant="additions"
    >
      {/* Header badge */}
      <div className="sticky top-0 right-0 flex justify-between items-center pb-3 sm:pb-4 z-20">
        <div className="bg-slate-900/80 backdrop-blur-md px-3 sm:px-4 py-1 rounded-full border border-slate-800 text-[7px] sm:text-[8px] font-black text-slate-500 uppercase tracking-widest">
          Diagnostic Feed
        </div>
        {isProcessing && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[9px] text-slate-500 hover:text-slate-300 uppercase tracking-widest transition-colors"
            aria-label="Collapse diagnostic log"
          >
            Collapse ↑
          </button>
        )}
      </div>

      {/* Log entries */}
      <div className="space-y-2 sm:space-y-3">
        {logs.map((log, index) => (
          <LogEntry
            key={log.id}
            log={log}
            isLast={index === logs.length - 1}
            lastLogRef={lastLogRef}
          />
        ))}

        {/* Empty state */}
        {logs.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full space-y-3 sm:space-y-4 py-6 sm:py-8 opacity-20">
            <div className="w-6 sm:w-8 h-6 sm:h-8 rounded-full border border-slate-800 flex items-center justify-center">
              <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 bg-slate-800 rounded-full animate-pulse" />
            </div>
            <div className="text-slate-700 font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.4em] sm:tracking-[0.5em]">
              Awaiting Input
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
